import { GAME_GRID_ACCENT_COLORS, GAME_TILE_COLORS } from "./themePalette";

export const RAVE_BORDER_LEVEL_MIN = 1;
export const RAVE_BORDER_LEVEL_MAX = 20;
export const RAVE_BORDER_WIDTH_MIN_PX = 12;
export const RAVE_BORDER_WIDTH_MAX_PX = 32;
export const RAVE_BORDER_FLICKER_MAX = 0.35;

export interface RaveBorderDefaults {
  // Legacy knob retained for compatibility; active rave coverage now uses full shell gutters.
  borderWidthPx: number;
  transitionFadeMs: number;
  grooveIntensityMult: number;
}

export const RAVE_BORDER_DEFAULTS: RaveBorderDefaults = {
  borderWidthPx: 24,
  transitionFadeMs: 300,
  grooveIntensityMult: 1.35
};

export const RAVE_EFFECT_IDS = [
  "grid_horizon_starfield",
  "grid_wave_floor",
  "grid_perspective_split",
  "grid_hyperplane",
  "box_tunnel_axis",
  "box_tunnel_twist",
  "box_tunnel_nested",
  "box_tunnel_lattice_chase",
  "radial_tube_spokes",
  "radial_tube_spiral",
  "radial_vortex_braid",
  "radial_blackhole_pre",
  "voxel_room_stack",
  "voxel_blocks_drift",
  "voxel_room_cascade",
  "voxel_particle_hybrid",
  "warp_mesh_ripple",
  "orbit_ring_rails",
  "orbit_ringfield_star",
  "blackhole_warp_finale"
] as const;

export type RaveEffectId = (typeof RAVE_EFFECT_IDS)[number];
export type RaveSymmetry = "mirror" | "complement";

export interface RaveBorderExtras {
  hazardStripeSpeed?: number;
  microArcRate?: "low" | "medium" | "high";
}

export interface RaveBorderLevelConfig {
  level: number;
  bpm: number;
  effectId: RaveEffectId;
  palette: string[];
  beatDiv: number;
  loopBeats?: number;
  speed: number;
  pulseStrength: number;
  pulseSharpness: number;
  glow: number;
  outerGlow: number;
  noise: number;
  rgbSplit: number;
  flicker: number;
  symmetry: RaveSymmetry;
  audioEnergyMult: number;
  transientMult: number;
  extras?: RaveBorderExtras;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function clampRaveLevel(level: number): number {
  return clamp(Math.floor(level), RAVE_BORDER_LEVEL_MIN, RAVE_BORDER_LEVEL_MAX);
}

export function clampRaveBorderWidthPx(widthPx: number): number {
  return clamp(widthPx, RAVE_BORDER_WIDTH_MIN_PX, RAVE_BORDER_WIDTH_MAX_PX);
}

export function clampRaveFlicker(value: number): number {
  return clamp(value, 0, RAVE_BORDER_FLICKER_MAX);
}

const FX_BLACK = "#000000";
const [
  TILE_CYAN,
  TILE_CORAL,
  TILE_GOLD,
  TILE_GREEN,
  TILE_PURPLE,
  TILE_MAGENTA,
  TILE_TEAL,
  TILE_ELECTRIC_CYAN,
  TILE_SKY_BLUE,
  TILE_LEMON,
  TILE_LAVENDER
] = GAME_TILE_COLORS;
const [, GRID_FRAME_BLUE, GRID_HUD_BLUE] = GAME_GRID_ACCENT_COLORS;

export const RAVE_BORDER_LEVEL_CONFIGS: ReadonlyArray<RaveBorderLevelConfig> = [
  { level: 1, bpm: 85, effectId: "grid_horizon_starfield", palette: [FX_BLACK, TILE_CYAN, TILE_LAVENDER], beatDiv: 1, loopBeats: 8, speed: 0.14, pulseStrength: 0.50, pulseSharpness: 3, glow: 0.72, outerGlow: 0.48, noise: 0.02, rgbSplit: 0.02, flicker: 0.02, symmetry: "mirror", audioEnergyMult: 0.16, transientMult: 0.08 },
  { level: 2, bpm: 90, effectId: "grid_wave_floor", palette: [FX_BLACK, TILE_GREEN, TILE_SKY_BLUE], beatDiv: 1, loopBeats: 8, speed: 0.20, pulseStrength: 0.54, pulseSharpness: 4, glow: 0.78, outerGlow: 0.52, noise: 0.03, rgbSplit: 0.03, flicker: 0.03, symmetry: "mirror", audioEnergyMult: 0.17, transientMult: 0.09 },
  { level: 3, bpm: 95, effectId: "grid_perspective_split", palette: [FX_BLACK, TILE_MAGENTA, TILE_CYAN], beatDiv: 2, loopBeats: 8, speed: 0.26, pulseStrength: 0.58, pulseSharpness: 4, glow: 0.82, outerGlow: 0.56, noise: 0.035, rgbSplit: 0.04, flicker: 0.04, symmetry: "complement", audioEnergyMult: 0.18, transientMult: 0.10 },
  { level: 4, bpm: 100, effectId: "grid_hyperplane", palette: [FX_BLACK, TILE_GOLD, GRID_HUD_BLUE], beatDiv: 2, loopBeats: 8, speed: 0.32, pulseStrength: 0.62, pulseSharpness: 5, glow: 0.86, outerGlow: 0.60, noise: 0.04, rgbSplit: 0.05, flicker: 0.05, symmetry: "complement", audioEnergyMult: 0.19, transientMult: 0.11 },
  { level: 5, bpm: 105, effectId: "box_tunnel_axis", palette: [FX_BLACK, TILE_CYAN, TILE_PURPLE], beatDiv: 2, loopBeats: 8, speed: 0.38, pulseStrength: 0.66, pulseSharpness: 5, glow: 0.90, outerGlow: 0.64, noise: 0.045, rgbSplit: 0.06, flicker: 0.06, symmetry: "mirror", audioEnergyMult: 0.20, transientMult: 0.12 },
  { level: 6, bpm: 110, effectId: "box_tunnel_twist", palette: [FX_BLACK, TILE_MAGENTA, TILE_ELECTRIC_CYAN], beatDiv: 2, loopBeats: 8, speed: 0.44, pulseStrength: 0.70, pulseSharpness: 6, glow: 0.94, outerGlow: 0.68, noise: 0.05, rgbSplit: 0.07, flicker: 0.07, symmetry: "mirror", audioEnergyMult: 0.22, transientMult: 0.13 },
  { level: 7, bpm: 115, effectId: "box_tunnel_nested", palette: [FX_BLACK, TILE_PURPLE, TILE_TEAL], beatDiv: 4, loopBeats: 8, speed: 0.52, pulseStrength: 0.74, pulseSharpness: 6, glow: 1.00, outerGlow: 0.72, noise: 0.06, rgbSplit: 0.08, flicker: 0.08, symmetry: "complement", audioEnergyMult: 0.23, transientMult: 0.14 },
  { level: 8, bpm: 120, effectId: "box_tunnel_lattice_chase", palette: [FX_BLACK, TILE_LEMON, TILE_CYAN], beatDiv: 4, loopBeats: 8, speed: 0.58, pulseStrength: 0.78, pulseSharpness: 7, glow: 1.04, outerGlow: 0.74, noise: 0.065, rgbSplit: 0.09, flicker: 0.09, symmetry: "complement", audioEnergyMult: 0.24, transientMult: 0.15 },
  { level: 9, bpm: 125, effectId: "radial_tube_spokes", palette: [FX_BLACK, TILE_ELECTRIC_CYAN, TILE_LAVENDER], beatDiv: 2, loopBeats: 12, speed: 0.66, pulseStrength: 0.80, pulseSharpness: 7, glow: 1.08, outerGlow: 0.76, noise: 0.07, rgbSplit: 0.10, flicker: 0.10, symmetry: "mirror", audioEnergyMult: 0.25, transientMult: 0.16 },
  { level: 10, bpm: 130, effectId: "radial_tube_spiral", palette: [FX_BLACK, TILE_CYAN, TILE_MAGENTA], beatDiv: 4, loopBeats: 12, speed: 0.74, pulseStrength: 0.84, pulseSharpness: 7, glow: 1.12, outerGlow: 0.80, noise: 0.075, rgbSplit: 0.11, flicker: 0.11, symmetry: "mirror", audioEnergyMult: 0.27, transientMult: 0.17 },
  { level: 11, bpm: 135, effectId: "radial_vortex_braid", palette: [FX_BLACK, TILE_TEAL, TILE_LAVENDER], beatDiv: 4, loopBeats: 12, speed: 0.82, pulseStrength: 0.88, pulseSharpness: 8, glow: 1.16, outerGlow: 0.84, noise: 0.085, rgbSplit: 0.12, flicker: 0.12, symmetry: "complement", audioEnergyMult: 0.28, transientMult: 0.18, extras: { microArcRate: "low" } },
  { level: 12, bpm: 140, effectId: "radial_blackhole_pre", palette: [FX_BLACK, TILE_GREEN, GRID_FRAME_BLUE], beatDiv: 4, loopBeats: 12, speed: 0.90, pulseStrength: 0.92, pulseSharpness: 8, glow: 1.20, outerGlow: 0.88, noise: 0.09, rgbSplit: 0.13, flicker: 0.13, symmetry: "complement", audioEnergyMult: 0.29, transientMult: 0.19 },
  { level: 13, bpm: 145, effectId: "voxel_room_stack", palette: [FX_BLACK, TILE_ELECTRIC_CYAN, TILE_MAGENTA], beatDiv: 4, loopBeats: 12, speed: 0.96, pulseStrength: 0.94, pulseSharpness: 8, glow: 1.24, outerGlow: 0.90, noise: 0.10, rgbSplit: 0.14, flicker: 0.14, symmetry: "mirror", audioEnergyMult: 0.30, transientMult: 0.20, extras: { microArcRate: "low" } },
  { level: 14, bpm: 150, effectId: "voxel_blocks_drift", palette: [FX_BLACK, TILE_CORAL, TILE_CYAN], beatDiv: 4, loopBeats: 12, speed: 1.02, pulseStrength: 0.97, pulseSharpness: 8, glow: 1.30, outerGlow: 0.94, noise: 0.105, rgbSplit: 0.15, flicker: 0.15, symmetry: "mirror", audioEnergyMult: 0.31, transientMult: 0.21, extras: { microArcRate: "medium" } },
  { level: 15, bpm: 155, effectId: "voxel_room_cascade", palette: [FX_BLACK, TILE_GREEN, TILE_SKY_BLUE], beatDiv: 8, loopBeats: 12, speed: 1.08, pulseStrength: 1.00, pulseSharpness: 9, glow: 1.36, outerGlow: 0.98, noise: 0.115, rgbSplit: 0.16, flicker: 0.16, symmetry: "complement", audioEnergyMult: 0.32, transientMult: 0.22, extras: { microArcRate: "medium" } },
  { level: 16, bpm: 160, effectId: "voxel_particle_hybrid", palette: [FX_BLACK, TILE_PURPLE, TILE_ELECTRIC_CYAN], beatDiv: 8, loopBeats: 12, speed: 1.14, pulseStrength: 1.02, pulseSharpness: 9, glow: 1.40, outerGlow: 1.00, noise: 0.12, rgbSplit: 0.17, flicker: 0.17, symmetry: "complement", audioEnergyMult: 0.33, transientMult: 0.23, extras: { microArcRate: "high" } },
  { level: 17, bpm: 165, effectId: "warp_mesh_ripple", palette: [FX_BLACK, TILE_CYAN, GRID_HUD_BLUE], beatDiv: 4, loopBeats: 16, speed: 1.18, pulseStrength: 1.03, pulseSharpness: 9, glow: 1.44, outerGlow: 1.02, noise: 0.125, rgbSplit: 0.18, flicker: 0.18, symmetry: "mirror", audioEnergyMult: 0.34, transientMult: 0.23, extras: { hazardStripeSpeed: 1.2, microArcRate: "medium" } },
  { level: 18, bpm: 170, effectId: "orbit_ring_rails", palette: [FX_BLACK, TILE_SKY_BLUE, TILE_GREEN], beatDiv: 8, loopBeats: 16, speed: 1.21, pulseStrength: 1.04, pulseSharpness: 9, glow: 1.48, outerGlow: 1.03, noise: 0.13, rgbSplit: 0.19, flicker: 0.20, symmetry: "mirror", audioEnergyMult: 0.35, transientMult: 0.24, extras: { hazardStripeSpeed: 1.3, microArcRate: "high" } },
  { level: 19, bpm: 175, effectId: "orbit_ringfield_star", palette: [FX_BLACK, TILE_CYAN, TILE_MAGENTA], beatDiv: 8, loopBeats: 16, speed: 1.23, pulseStrength: 1.04, pulseSharpness: 10, glow: 1.52, outerGlow: 1.04, noise: 0.135, rgbSplit: 0.20, flicker: 0.21, symmetry: "complement", audioEnergyMult: 0.36, transientMult: 0.24, extras: { hazardStripeSpeed: 1.4, microArcRate: "high" } },
  { level: 20, bpm: 180, effectId: "blackhole_warp_finale", palette: [FX_BLACK, TILE_MAGENTA, TILE_CYAN, TILE_LEMON], beatDiv: 8, loopBeats: 16, speed: 1.25, pulseStrength: 1.05, pulseSharpness: 10, glow: 1.55, outerGlow: 1.05, noise: 0.14, rgbSplit: 0.20, flicker: 0.22, symmetry: "complement", audioEnergyMult: 0.36, transientMult: 0.24, extras: { hazardStripeSpeed: 1.5, microArcRate: "high" } }
];

export function getRaveBorderLevelConfig(level: number): RaveBorderLevelConfig {
  const clampedLevel = clampRaveLevel(level);
  const config = RAVE_BORDER_LEVEL_CONFIGS[clampedLevel - 1];
  if (config == null) {
    return RAVE_BORDER_LEVEL_CONFIGS[0] as RaveBorderLevelConfig;
  }
  return config;
}
